import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

/*
  RANSEN Acronym Scroll Section
  --------------------------------
  Letters R A N S E N start wide across the viewport, converge to center as the user scrolls,
  then reveal the meaning of each letter and a supporting paragraph.
*/

const LETTERS = [
  {
    char: 'R',
    label: 'Research',
    blurb: 'We dig into markets, competitors and audience psychology.',
  },
  {
    char: 'A',
    label: 'Analytics',
    blurb: 'Every decision is backed by data, not gut feel.',
  },
  {
    char: 'N',
    label: 'Navigation',
    blurb: 'We chart the sharpest route from where you are to where you want to be.',
  },
  {
    char: 'S',
    label: 'Strategy',
    blurb: 'A tailored playbook — brand, growth, funnels, creative.',
  },
  {
    char: 'E',
    label: 'Execution',
    blurb: 'Elite creative, engineering and media buying, shipped fast.',
  },
  {
    char: 'N',
    label: 'Next',
    blurb: 'Iterate, optimise, compound — we build for what comes next.',
  },
];

// Wide starting X positions (in viewport width units) — spread far edge to edge, converge to 0.
// We keep them symmetric around 0 so they land next to each other centered on screen.
const START_OFFSETS_VW = [-46, -30, -14, 12, 28, 44];

const RansenLetter = ({ index, char, progress }) => {
  // Each letter animates its horizontal offset from the wide starting vw to 0.
  const startVW = START_OFFSETS_VW[index];

  // Letters converge during the first 55% of scroll, then hold.
  const x = useTransform(progress, [0, 0.55, 1], [`${startVW}vw`, '0vw', '0vw']);

  // Slight vertical drift + fade-in for cinematic feel.
  const y = useTransform(progress, [0, 0.4, 0.55, 1], [40, 10, 0, 0]);
  const opacity = useTransform(progress, [0, 0.08, 0.55, 1], [0, 1, 1, 1]);

  // Outlined -> filled: text becomes solid gradient after convergence.
  const fillOpacity = useTransform(progress, [0.5, 0.72], [0, 1]);
  const strokeOpacity = useTransform(progress, [0.5, 0.72], [1, 0.2]);

  // Slight scale-up on lock.
  const scale = useTransform(progress, [0, 0.55, 0.7, 1], [1.1, 1, 1.02, 1]);

  return (
    <motion.div
      style={{ x, y, opacity, scale }}
      className="relative inline-flex flex-col items-center justify-center"
    >
      <div className="relative leading-none">
        {/* Outlined layer */}
        <motion.span
          style={{ opacity: strokeOpacity, WebkitTextStroke: '1.5px rgba(255,255,255,0.55)' }}
          className="font-heading font-black text-[15vw] md:text-[16vw] lg:text-[13vw] block"
          aria-hidden="true"
        >
          <span style={{ color: 'transparent' }}>{char}</span>
        </motion.span>
        {/* Gradient-filled layer (fades in after lock) */}
        <motion.span
          style={{
            opacity: fillOpacity,
            backgroundImage:
              'linear-gradient(135deg, #8A2BE2 0%, #FF0080 55%, #00F0FF 100%)',
            WebkitBackgroundClip: 'text',
            backgroundClip: 'text',
            color: 'transparent',
            filter: 'drop-shadow(0 8px 40px rgba(138,43,226,0.35))',
          }}
          className="font-heading font-black text-[15vw] md:text-[16vw] lg:text-[13vw] block absolute inset-0"
        >
          {char}
        </motion.span>
      </div>
    </motion.div>
  );
};

const RansenAcronym = () => {
  const wrapperRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: wrapperRef,
    offset: ['start start', 'end end'],
  });

  // Progressive reveal windows for the details block.
  const labelsOpacity = useTransform(scrollYProgress, [0.6, 0.78], [0, 1]);
  const labelsY = useTransform(scrollYProgress, [0.6, 0.78], [24, 0]);
  const meaningOpacity = useTransform(scrollYProgress, [0.8, 0.95], [0, 1]);
  const meaningY = useTransform(scrollYProgress, [0.8, 0.95], [24, 0]);

  // Ambient orbs drift subtly with scroll.
  const orb1Y = useTransform(scrollYProgress, [0, 1], ['-20%', '30%']);
  const orb2Y = useTransform(scrollYProgress, [0, 1], ['30%', '-20%']);

  return (
    <section
      ref={wrapperRef}
      data-testid="ransen-acronym-section"
      className="relative w-full bg-void"
      style={{ height: '260vh' }}
    >
      <div className="sticky top-0 h-screen w-full flex items-center justify-center overflow-hidden">
        {/* Ambient background */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(138,43,226,0.18),transparent_65%)]" />
          <motion.div
            style={{ y: orb1Y }}
            className="absolute -left-32 top-1/4 h-[420px] w-[420px] rounded-full blur-3xl"
          >
            <div className="w-full h-full rounded-full bg-brand/25" />
          </motion.div>
          <motion.div
            style={{ y: orb2Y }}
            className="absolute -right-32 bottom-1/4 h-[420px] w-[420px] rounded-full blur-3xl"
          >
            <div className="w-full h-full rounded-full bg-accent-pink/20" />
          </motion.div>
          <div className="absolute inset-0 opacity-[0.05]" style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
            backgroundSize: '32px 32px',
          }} />
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* Eyebrow */}
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6 }}
            className="font-accent text-xs md:text-sm tracking-[0.4em] uppercase text-white/60 mb-6"
            data-testid="ransen-acronym-eyebrow"
          >
            The Meaning Behind The Name
          </motion.p>

          {/* Letters */}
          <div className="flex items-end justify-center gap-[0.5vw]" data-testid="ransen-letters">
            {LETTERS.map((l, i) => (
              <RansenLetter key={i} index={i} char={l.char} progress={scrollYProgress} />
            ))}
          </div>

          {/* Sub-labels row */}
          <motion.div
            style={{ opacity: labelsOpacity, y: labelsY }}
            className="mt-6 md:mt-10 grid grid-cols-3 md:grid-cols-6 gap-3 md:gap-4"
            data-testid="ransen-labels"
          >
            {LETTERS.map((l, i) => (
              <div
                key={i}
                className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-sm px-2 py-3 md:px-3 md:py-4"
              >
                <div className="font-accent text-[10px] md:text-xs tracking-widest text-accent-cyan uppercase mb-1">
                  {l.char}
                </div>
                <div className="font-heading text-sm md:text-lg font-bold text-white leading-tight">
                  {l.label}
                </div>
                <div className="hidden md:block text-white/60 text-xs mt-1 leading-snug">
                  {l.blurb}
                </div>
              </div>
            ))}
          </motion.div>

          {/* Meaning paragraph */}
          <motion.div
            style={{ opacity: meaningOpacity, y: meaningY }}
            className="mt-8 md:mt-12 max-w-3xl mx-auto"
            data-testid="ransen-meaning"
          >
            <p className="text-white/80 text-base md:text-lg leading-relaxed">
              We <span className="text-white font-semibold">research</span> markets, decode
              audiences, and lean on{' '}
              <span className="text-white font-semibold">analytics</span> to see what actually
              moves the needle. From there we{' '}
              <span className="text-white font-semibold">navigate</span> the noise with a
              sharp <span className="text-white font-semibold">strategy</span>, ship elite{' '}
              <span className="text-white font-semibold">execution</span> across brand, growth
              and creative — then obsess over what comes{' '}
              <span className="text-white font-semibold">next</span>. That&apos;s RANSEN.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default RansenAcronym;
